/**
 * Bridge between a finalized brush gesture (in render coordinates) and the
 * User Zoom contract (in axis coordinates).
 *
 * The line plot's brush highlight emits a `DomainArea` whose x bounds are in
 * the chart's *rendering* coordinate space — `Timestep` units when the axis is
 * displayed in minutes/hours/days, or milliseconds-since-epoch for datetime
 * axes. The User Zoom payload (`UserZoomIntentPayload`) is contractually
 * stored in seconds (or unix seconds for `_timestamp`). This file is the only
 * place that conversion happens for zoom gestures; without it, persisted zoom
 * state would round-trip back to the wrong domain.
 *
 * See `./PRODUCT_RULES.md` for the user-visible behavior, `./ARCHITECTURE.md`
 * for the unit story across x-axis types, and `./zoomIntent.ts` for the
 * intent-driven patch semantics that the payload feeds into.
 */
import {convertTimestepToSeconds} from '../../../util/plotHelpers/time';
import {type Timestep} from '../../../util/plotHelpers/types';
import {
  type BrushEndMetadata,
  type BrushZoomIntent,
  type DomainArea,
} from '../../vis/highlight';
import {type UserZoomIntentPayload} from '../types';

type BrushAreaToUserZoomParams = {
  area: DomainArea | null;
  timestep?: Timestep;
  xAxisIsDatetime: boolean;
  zoomIntent: BrushZoomIntent;
};

type FinalizeBrushDragParams = BrushAreaToUserZoomParams & {
  brushEndMetadata?: BrushEndMetadata;
  interrupted?: boolean;
  setLastDrawLocation: (area: DomainArea | null) => void;
  zoomCallback?: (
    zoom: UserZoomIntentPayload,
    brushEndMetadata?: BrushEndMetadata
  ) => void;
};

/**
 * Translate a brush rectangle into a User Zoom payload.
 *
 * The intent (which axes the gesture targets) is preserved on the payload so
 * downstream consumers (`buildUserZoomPatch` in `./zoomIntent.ts`) can apply
 * the asymmetric preserve/clear rules from `./PRODUCT_RULES.md`.
 *
 * A null `area` means the gesture produced no domain rectangle — typically a
 * reset click. The result is an intent-only payload, which `buildUserZoomPatch`
 * resolves to a full reset when no axes are active.
 *
 * Only x bounds are unit-converted: brush x-coords arrive in render space
 * (`Timestep` units or ms-since-epoch for datetime), and the User Zoom
 * contract requires seconds. Y bounds pass through unchanged because the
 * y-axis has no comparable unit translation in this codebase.
 */
export function brushAreaToUserZoom({
  area,
  timestep,
  xAxisIsDatetime,
  zoomIntent,
}: BrushAreaToUserZoomParams): UserZoomIntentPayload {
  if (area == null) {
    return {intent: zoomIntent};
  }

  const zoom: UserZoomIntentPayload = {intent: zoomIntent};

  if (zoomIntent.shouldZoomX) {
    let left = area.left;
    let right = area.right;
    if (timestep != null) {
      // Render coords use the displayed Timestep unit (minutes/hours/days).
      // Multiply back to seconds for the User Zoom contract.
      left = convertTimestepToSeconds(left, timestep);
      right = convertTimestepToSeconds(right, timestep);
    } else if (xAxisIsDatetime) {
      // Datetime axes render in ms-since-epoch (JS Date convention) but the
      // User Zoom contract stores unix seconds. See ARCHITECTURE.md
      // "x-axis units" table.
      left = left / 1000;
      right = right / 1000;
    }
    zoom.xAxisMin = left;
    zoom.xAxisMax = right;
  }

  if (zoomIntent.shouldZoomY) {
    // DomainArea uses screen-space orientation (top < bottom numerically) so
    // the visual top of the brush is the larger axis value.
    zoom.yAxisMin = area.bottom;
    zoom.yAxisMax = area.top;
  }

  return zoom;
}

/**
 * Commit a finalized brush gesture: persist the draw rectangle for re-render
 * and dispatch the resulting User Zoom payload to the panel.
 *
 * `interrupted` short-circuits both side effects — used when the brush is
 * cancelled mid-gesture (e.g., escape key, pointer leaves the chart context)
 * so neither the visual highlight nor the zoom state advances.
 */
export function finalizeBrushDrag({
  area,
  brushEndMetadata,
  interrupted,
  setLastDrawLocation,
  timestep,
  xAxisIsDatetime,
  zoomCallback,
  zoomIntent,
}: FinalizeBrushDragParams): void {
  if (interrupted) {
    return;
  }

  setLastDrawLocation(area);

  if (zoomCallback == null) {
    return;
  }

  zoomCallback(
    brushAreaToUserZoom({
      area,
      timestep,
      xAxisIsDatetime,
      zoomIntent,
    }),
    brushEndMetadata
  );
}
